import {
  useRealtimeKitMeeting,
  useRealtimeKitSelector,
} from "@cloudflare/realtimekit-react";
import { MeetingLogoutButton } from "./MeetingLogoutButton";
import { LivestreamHostSidebar } from "./LivestreamHostSidebar";
import { LivestreamVideoGrid } from "./LivestreamVideoGrid";
import { LivestreamViewerFrameOverlay } from "./LivestreamViewerFrameOverlay";
import { useLivestreamChat } from "./useLivestreamChat";
import { useLivestreamStageFeeds } from "./useLivestreamStageFeeds";
import { useLivestreamStageHost } from "./useLivestreamStageHost";

export function LivestreamHostView({
  showLogout,
  loggingOut,
  onLogout,
}: {
  showLogout: boolean;
  loggingOut: boolean;
  onLogout: () => void;
}) {
  const { meeting } = useRealtimeKitMeeting();
  const selfId = useRealtimeKitSelector((m) => m.self.id);
  const roomState = useRealtimeKitSelector((m) => m.self.roomState);
  const videoEnabled = useRealtimeKitSelector((m) => m.self.videoEnabled);
  const audioEnabled = useRealtimeKitSelector((m) => m.self.audioEnabled);
  const screenShareEnabled = useRealtimeKitSelector((m) => m.self.screenShareEnabled);
  const canShareScreen =
    useRealtimeKitSelector((m) => m.self.permissions.canProduceScreenshare) === "ALLOWED";
  const canRecord = useRealtimeKitSelector((m) => m.self.permissions.canRecord);
  const recordingState = useRealtimeKitSelector((m) => m.recording.recordingState);
  const isRecordingActive =
    recordingState === "RECORDING" ||
    recordingState === "PAUSED" ||
    recordingState === "STARTING";
  const recordingBusy =
    recordingState === "STARTING" || recordingState === "STOPPING";
  const showLogoutButton = showLogout && roomState !== "joined";
  const { messages, sendMessage } = useLivestreamChat({ meeting });
  const stageFeeds = useLivestreamStageFeeds({ meeting, includeSelf: true });
  const {
    participants,
    stageRequests,
    grantAccess,
    denyAccess,
    removeFromStage,
    actingOnUserId,
  } = useLivestreamStageHost({ meeting });
  const hasStageScreenShare = stageFeeds.some(
    (participant) =>
      participant.screenShareEnabled && Boolean(participant.screenShareTracks?.video),
  );
  const showViewerFrameOverlay =
    stageFeeds.length === 1 && !hasStageScreenShare;

  if (!meeting) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-black text-white">
        Connecting...
      </div>
    );
  }

  return (
    <div className="flex h-dvh w-screen bg-black">
      <div className="@container relative min-w-0 flex-1 [container-type:size]">
        {showLogoutButton ? (
          <MeetingLogoutButton
            showLogout={showLogoutButton}
            loggingOut={loggingOut}
            onLogout={onLogout}
          />
        ) : null}
        <LivestreamVideoGrid participants={stageFeeds} selfId={selfId} />
        {showViewerFrameOverlay ? <LivestreamViewerFrameOverlay /> : null}
      </div>
      <LivestreamHostSidebar
        messages={messages}
        onSendMessage={sendMessage}
        participants={participants}
        stageRequests={stageRequests}
        onGrantStageAccess={async ({ userId }) => {
          if (hasStageScreenShare) return;
          await grantAccess({ userId });
        }}
        onDenyStageAccess={denyAccess}
        onRemoveFromStage={removeFromStage}
        actingOnUserId={actingOnUserId}
        canGrantStageAccess={!hasStageScreenShare}
        audioEnabled={audioEnabled}
        videoEnabled={videoEnabled}
        screenShareEnabled={screenShareEnabled}
        canShareScreen={canShareScreen}
        canRecord={canRecord}
        isRecordingActive={isRecordingActive}
        recordingBusy={recordingBusy}
        onToggleRecording={() => {
          if (recordingBusy) return;
          if (isRecordingActive) {
            void meeting.recording.stop();
          } else {
            void meeting.recording.start();
          }
        }}
        onToggleAudio={() => {
          if (audioEnabled) {
            void meeting.self.disableAudio();
          } else {
            void meeting.self.enableAudio();
          }
        }}
        onToggleVideo={() => {
          if (videoEnabled) {
            void meeting.self.disableVideo();
          } else {
            void meeting.self.enableVideo();
          }
        }}
        onToggleScreenShare={() => {
          if (screenShareEnabled) {
            void meeting.self.disableScreenShare();
          } else {
            void meeting.self.enableScreenShare();
          }
        }}
      />
    </div>
  );
}
